import { useState } from 'react';
import { AdminConfirmDialog } from '../../components/primitives/admin-confirm-dialog.js';
import { AdminSelect } from '../../components/primitives/admin-select.js';
import type { AdminBrand, AdminCategory, BulkProductAction } from '../types/catalog.js';

export interface BulkActionToolbarProps {
  selectedCount: number;
  categories: AdminCategory[];
  brands: AdminBrand[];
  onRun: (action: BulkProductAction, productIds: string[]) => void;
  productIds: string[];
  isRunning: boolean;
  onClear: () => void;
}

type SimpleAction = 'activate' | 'archive' | 'feature' | 'unfeature';

/** Bounded to the strict backend allowlist (Part 14) -- every button here maps to exactly one `BulkProductAction` variant, never an arbitrary field patch. */
export function BulkActionToolbar({
  selectedCount,
  categories,
  brands,
  onRun,
  productIds,
  isRunning,
  onClear,
}: BulkActionToolbarProps) {
  const [pendingAction, setPendingAction] = useState<SimpleAction | null>(null);
  const [categoryChoice, setCategoryChoice] = useState('');
  const [brandChoice, setBrandChoice] = useState('');

  if (selectedCount === 0) return null;

  function confirmSimple(action: SimpleAction) {
    onRun({ action, productIds }, productIds);
    setPendingAction(null);
  }

  return (
    <div className="mb-4 flex flex-wrap items-center gap-2 rounded-2xl border border-gray-200 bg-gray-50 px-4 py-2.5">
      <span className="text-sm font-medium text-gray-700">{selectedCount} selected</span>
      <button
        type="button"
        onClick={() => setPendingAction('activate')}
        disabled={isRunning}
        className="rounded-full border border-gray-200 bg-white px-3 py-1 text-sm hover:bg-gray-50 disabled:opacity-60"
      >
        Activate
      </button>
      <button
        type="button"
        onClick={() => setPendingAction('archive')}
        disabled={isRunning}
        className="rounded-full border border-gray-200 bg-white px-3 py-1 text-sm hover:bg-gray-50 disabled:opacity-60"
      >
        Archive
      </button>
      <button
        type="button"
        onClick={() => setPendingAction('feature')}
        disabled={isRunning}
        className="rounded-full border border-gray-200 bg-white px-3 py-1 text-sm hover:bg-gray-50 disabled:opacity-60"
      >
        Mark featured
      </button>
      <button
        type="button"
        onClick={() => setPendingAction('unfeature')}
        disabled={isRunning}
        className="rounded-full border border-gray-200 bg-white px-3 py-1 text-sm hover:bg-gray-50 disabled:opacity-60"
      >
        Remove featured
      </button>

      <div className="flex items-center gap-1">
        <AdminSelect
          label="Assign category"
          value={categoryChoice}
          onChange={(value) => {
            setCategoryChoice(value);
            if (value) {
              onRun({ action: 'assignCategory', productIds, categoryId: value }, productIds);
              setCategoryChoice('');
            }
          }}
          allLabel="Assign category…"
          options={categories.map((c) => ({ value: c.id, label: c.name }))}
        />
      </div>
      <div className="flex items-center gap-1">
        <AdminSelect
          label="Assign brand"
          value={brandChoice}
          onChange={(value) => {
            setBrandChoice(value);
            if (value) {
              onRun({ action: 'assignBrand', productIds, brandId: value }, productIds);
              setBrandChoice('');
            }
          }}
          allLabel="Assign brand…"
          options={brands.map((b) => ({ value: b.id, label: b.name }))}
        />
      </div>

      <button
        type="button"
        onClick={onClear}
        className="ml-auto rounded-full px-3 py-1 text-sm text-gray-500 hover:bg-gray-100"
      >
        Clear selection
      </button>

      <AdminConfirmDialog
        open={pendingAction !== null}
        title={`${pendingAction ?? ''} ${selectedCount} product(s)?`}
        description="This applies to every selected product. Partial failures (e.g. a product that no longer exists) are reported individually and do not block the rest."
        confirmLabel="Confirm"
        isConfirming={isRunning}
        onConfirm={() => pendingAction && confirmSimple(pendingAction)}
        onCancel={() => setPendingAction(null)}
      />
    </div>
  );
}
